import React from 'react';
import isPropValid from '@emotion/is-prop-valid';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';
import styled, { css } from 'styled-components';
import { colors, spacing, rgba } from '../../style';

const variants = {
  main: css`
    color: ${colors.white};

    &:hover:not([disabled]) {
      background: ${rgba(colors.gray900, 0.12)};
    }

    &:active,
    &:active:hover,
    &.active {
      background: ${rgba(colors.gray900, 0.24)};
    }

    &.active {
      &:hover {
        background: ${rgba(colors.gray900, 0.24)};
      }
    }
  `,
  inverse: css`
    color: ${colors.gray900};

    &:hover:not([disabled]) {
      background: ${colors.gray100};
    }

    &:active,
    &:active:hover,
    &.active {
      background: ${props => props.theme.actionLight};
      color: ${props => props.theme.actionColor};
    }
  `,
};

const sizes = {
  md: '40px',
};

const shapes = {
  default: '',
  square: css`
    width: ${sizes.md};
    padding: 0;
    justify-content: center;
  `,
};

const StyledNavButton = styled.button.withConfig({ shouldForwardProp: isPropValid })<NavButtonProps>`
  display: flex;
  height: ${sizes.md};
  padding: 8px 12px;
  margin-left: ${props => spacing[props.marginHorizontal || 'none']};
  margin-right: ${props => spacing[props.marginHorizontal || 'none']};
  align-items: center;
  border-radius: 4px;
  font-weight: 400;
  border: none;
  background: transparent;
  transition: 0.2s;

  &:not([disabled]) {
    cursor: pointer;
  }

  &[disabled] {
    cursor: not-allowed;
    background: ${colors.gray400};
    color: ${colors.gray900};
  }

  ${(props: NavButtonProps) => variants[props.variant || 'main']};
  ${(props: NavButtonProps) => shapes[props.shape || 'default']};

  &:focus-visible {
    border: 1px solid ${colors.sky};
  }

  &.active {
    &,
    > * {
      font-weight: 500;
    }
  }
`;

export interface NavButtonProps {
  isColored?: boolean;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  to?: string;
  children?: React.ReactNode | string;
  href?: string;
  variant?: keyof typeof variants;
  componentClass?: React.ElementType;
  shape?: keyof typeof shapes;
  disabled?: boolean;
  active?: boolean;
  exact?: boolean;
  marginHorizontal?: keyof typeof spacing;
}

const NavButton = React.forwardRef(({ className, to, href, isColored, active, ...rest }: NavButtonProps, ref) => {
  return (
    <StyledNavButton
      // @ts-expect-error
      ref={ref}
      className={classNames('navbar-button', className, { active })}
      size={'md'}
      as={href ? 'a' : to ? NavLink : undefined}
      to={to}
      weight={400}
      href={href}
      {...rest}
      variant={isColored ? 'main' : 'inverse'}
    />
  );
});

NavButton.displayName = 'NavButton';
export default NavButton;
